import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Circuit } from '@/types';
import { TripPreviewCard } from './TripPreviewCard';

interface StackedCarouselProps {
    circuits: Circuit[];
    onSelect: (circuit: Circuit) => void;
}

export function StackedCarousel({ circuits, onSelect }: StackedCarouselProps) {
    const [index, setIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const dragX = useMotionValue(0);
    const containerRef = useRef<HTMLDivElement>(null);

    // Detect mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Reset index when circuits change (use circuits.length and first circuit id to detect changes)
    useEffect(() => {
        setIndex(0);
        setIsDragging(false);
        dragX.set(0);
    }, [circuits.length, circuits[0]?.id, circuits.map(c => c.id).join(',')]);

    const nextCard = () => {
        setIndex((prev) => (prev + 1) % circuits.length);
    };

    const prevCard = () => {
        setIndex((prev) => (prev - 1 + circuits.length) % circuits.length);
    };

    // Touch/swipe handling
    const handleDragEnd = () => {
        const threshold = 50;
        const currentX = dragX.get();
        
        if (Math.abs(currentX) > threshold) {
            if (currentX > 0) {
                prevCard();
            } else {
                nextCard();
            }
        }
        
        dragX.set(0);
        setIsDragging(false);
    };

    // Determine indices for previous, current, and next cards
    // Determine indices for previous, current, and next cards
    // const getIndex = (i: number) => {
    //    const len = circuits.length;
    //    return (i + len) % len;
    // };

    // Logic to show a "stack"
    // If we have only 1 card, just show it.
    // If we have multiple, we want to render them all but positioned absolutely?
    // Or just render a window of them.
    // Rendering all is safer for small numbers (e.g. < 10) which is our case.

    return (
        <div 
            ref={containerRef}
            className="relative w-full md:w-[600px] h-[480px] sm:h-[540px] md:h-[450px] flex items-center justify-center perspective-1000 overflow-visible"
        >
            <AnimatePresence mode="popLayout">
                {circuits.map((circuit, i) => {
                    // Calculate relative distance with wrap-around
                    const length = circuits.length;

                    // We need a signed distance to know if something is "next" or "prev"
                    // But in a circular list, "prev" limit is ambiguous.
                    // Let's assume a window. 
                    // To simplify: We find the shortest distance.

                    let diff = i - index;

                    // Adjust for wrap-around to handle circular navigation
                    // Standard circular buffer distance calculation
                    // This ensures smooth transitions without duplicates
                    if (diff > length / 2) diff -= length;
                    if (diff < -length / 2) diff += length;

                    // Now:
                    // diff = 0: Active
                    // diff > 0: Next (Right)
                    // diff < 0: Prev (Left)

                    let zIndex = 0;
                    let x = 0;
                    let scale = 0.8;
                    let opacity = 0;
                    let rotateY = 0;

                    if (diff === 0) {
                        // Active - Center
                        zIndex = 30;
                        x = 0;
                        scale = 1;
                        opacity = 1; // Fully visible
                        rotateY = 0;
                    } else if (diff > 0) {
                        // Next - Stacked on Right
                        zIndex = 20 - diff; // Further ones are behind
                        // Stack effect: shift right by constant + small offset per item
                        // Reduced offset on mobile to prevent overflow
                        x = isMobile ? 20 + (diff * 8) : 40 + (diff * 15);
                        scale = 1 - (diff * 0.05); // shrinking slightly
                        opacity = 1 - (diff * 0.15); // Fading slightly but visible
                        if (opacity < 0) opacity = 0; // Safety
                        // Disable 3D rotation on mobile for better performance
                        rotateY = isMobile ? 0 : -5; // Tilted towards center
                        
                        // Hide cards beyond 2 on mobile to prevent overflow
                        if (isMobile && diff > 2) {
                            opacity = 0;
                        }
                    } else {
                        // Prev - Hidden on Left
                        // We keep them "there" for animation physics but invisible
                        zIndex = 10;
                        x = isMobile ? -80 : -100; // Moved left
                        scale = 0.8;
                        opacity = 0; // HIDDEN
                        rotateY = isMobile ? 0 : 5;
                    }

                    // Allow previous one to be slightly visible if it's strictly -1?
                    // User said: "0 en face puis Les autres cartes empiler à droite"
                    // This implies only 0 and Right are visible. Left is history.
                    // But for "avancer puis revenir", we need to be able to go back.
                    // We handle this via the control buttons calling prevCard().
                    // When prevCard() triggers, index changes, and the one at -1 becomes 0 (and becomes visible).

                    return (
                        <motion.div
                            key={circuit.id}
                            className="absolute top-1/2 left-1/2 isolate"
                            style={{
                                width: '100%',
                                maxWidth: '24rem',
                                x: '-50%',
                                y: '-50%'
                            }}
                            animate={{
                                zIndex,
                                x: `calc(-50% + ${x}px)`,
                                scale,
                                opacity,
                                rotateY
                            }}
                            transition={isMobile ? {
                                type: "tween",
                                duration: 0.3,
                                ease: "easeOut"
                            } : {
                                type: "spring", 
                                stiffness: 100, 
                                damping: 20, 
                                mass: 1
                            }}
                            // Swipe support
                            drag={diff === 0 ? "x" : false}
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.2}
                            onDragStart={() => setIsDragging(true)}
                            onDrag={(_, info) => {
                                if (diff === 0) {
                                    dragX.set(info.offset.x);
                                }
                            }}
                            onDragEnd={handleDragEnd}
                            onClick={() => {
                                if (!isDragging) {
                                    if (diff === 0) onSelect(circuit);
                                    else if (diff > 0) nextCard();
                                    else if (diff < 0) prevCard();
                                }
                            }}
                        >
                            <div className={diff === 0 ? 'pointer-events-auto' : 'pointer-events-none md:pointer-events-auto cursor-pointer'}>
                                <TripPreviewCard
                                    circuit={circuit}
                                    onClick={() => { }}
                                    className="w-full shadow-2xl"
                                />
                                {/* Overlay only for right stack items to distinguish depth */}
                                {diff > 0 && <div className="absolute inset-0 bg-black/10 rounded-2xl" />}
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>

            {/* Navigation Controls - Sur mobile écartés, sur desktop au bord des cartes (24rem = largeur carte) */}
            {circuits.length > 1 && (
                <>
                    {/* Bouton Précédent - Gauche */}
                    <button
                        onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation(); 
                            prevCard(); 
                        }}
                        className="absolute -left-5 top-1/2 -translate-y-1/2 md:left-[calc(50%-12rem-1.375rem)] md:right-auto md:translate-x-0 z-[100] p-2 md:p-2.5 min-w-[38px] min-h-[38px] md:min-w-[44px] md:min-h-[44px] flex items-center justify-center bg-white/15 hover:bg-white/25 active:bg-white/35 backdrop-blur-lg rounded-full text-white transition-all border-2 border-white/35 shadow-2xl touch-manipulation pointer-events-auto group"
                        aria-label="Circuit précédent"
                        type="button"
                    >
                        <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 group-active:scale-90 transition-transform" strokeWidth={3} />
                    </button>

                    {/* Bouton Suivant - Droite */}
                    <button
                        onClick={(e) => { 
                            e.preventDefault();
                            e.stopPropagation(); 
                            nextCard(); 
                        }}
                        className="absolute -right-5 top-1/2 -translate-y-1/2 md:right-[calc(50%-12rem-2.75rem)] md:left-auto md:translate-x-0 z-[100] p-2 md:p-2.5 min-w-[38px] min-h-[38px] md:min-w-[44px] md:min-h-[44px] flex items-center justify-center bg-white/15 hover:bg-white/25 active:bg-white/35 backdrop-blur-lg rounded-full text-white transition-all border-2 border-white/35 shadow-2xl touch-manipulation pointer-events-auto group"
                        aria-label="Circuit suivant"
                        type="button"
                    >
                        <ChevronRight className="w-4 h-4 md:w-5 md:h-5 group-active:scale-90 transition-transform" strokeWidth={3} />
                    </button>

                    {/* Indicateur de pagination - Plus haut pour ne pas empiéter sur Quick Replies */}
                    <div className="absolute bottom-20 md:bottom-16 left-1/2 -translate-x-1/2 z-[90] pointer-events-none">
                        <span className="inline-block text-white text-xs md:text-sm font-semibold tracking-wider px-3 py-1.5 bg-black/40 backdrop-blur-md rounded-full border border-white/20 shadow-lg">
                            {index + 1} / {circuits.length}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
